import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SummaryModal from '../Modals/SummaryModal';
import ClientAnalytics from './ClientAnalytics';
import { useAuth } from '../../services/AuthContext';
import { referralAPI } from '../../services/api';

const ClientRow = ({ client, insights = {}, memory = {}, summary = null }) => {
  const [expanded, setExpanded] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [showClientStats, setShowClientStats] = useState(false);
  const [followUp, setFollowUp] = useState(client.followUp || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'idle', 'saving', 'success', 'error'
  const { user } = useAuth();

  const handleSaveFollowUp = async () => {
    if (!client.clientId) return;
    setIsSaving(true);
    setSaveStatus('saving');
    try {
      await referralAPI.updateFollowUp(client.clientId, followUp);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving follow-up:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper for safe access to insight/memory fields
  const getInsight = (field) => (insights && insights[field]) ? insights[field] : 'No data available';
  const getMemory = (field) => (memory && memory[field]) ? memory[field] : 'No data available';

  return (
    <div className={`bg-[var(--surface-white)] rounded-lg border transition-all group overflow-hidden ${expanded ? 'border-[var(--tangerine)] shadow-md ring-1 ring-[var(--tangerine)]/20' : 'border-[var(--border-light)] hover:border-[var(--tangerine)]'}`}>
      <div 
        className={`p-4 grid grid-cols-12 gap-4 items-center cursor-pointer ${expanded ? 'border-b border-[var(--border-light)] bg-[var(--tangerine)]/10' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="col-span-4 flex items-center gap-3">
          {client.avatar ? (
            <img alt="Client" className="w-10 h-10 rounded-full object-cover border border-[var(--border-light)]" src={client.avatar} />
          ) : (
            <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-[var(--tangerine)] border border-orange-200">
              <span className="material-symbols-outlined filled-icon text-xl">person</span>
            </div>
          )}
          <div>
            <h3 className="text-sm font-bold text-[var(--text-primary)]">{client.name}</h3>
            <p className="text-xs text-[var(--text-secondary)]">{client.title}</p>
          </div>
        </div>
        <div className="col-span-3">
          <div className="flex items-center gap-4 text-[var(--text-secondary)]/60 text-[10px] font-medium">
            <div className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">calendar_today</span>
              <span className="leading-none">{client.lastInteractionTime}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">{client.lastInteractionTypeIcon}</span>
              <span className="leading-none">{client.lastInteractionType}</span>
            </div>
            <div className="flex items-center gap-1 text-[var(--tangerine)] font-bold uppercase tracking-tight">
              <span className="material-symbols-outlined text-[14px]">trending_up</span>
              <span className="leading-none">{client.visits}</span>
            </div>
          </div>
        </div>
        <div className="col-span-5 flex items-center justify-end gap-3">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowClientStats(!showClientStats); if(!expanded) setExpanded(true); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
              showClientStats 
              ? 'bg-[var(--tangerine)] text-white shadow-md shadow-tangerine/20' 
              : 'text-[var(--text-secondary)]/60 hover:text-[var(--tangerine)] bg-[var(--off-white)] border border-[var(--border-light)]'
            }`}
          >
            <span className="material-symbols-outlined text-[16px]">analytics</span>
            Stats
          </button>
          <button className={`text-xs font-medium flex items-center transition-colors ${expanded ? 'text-[var(--tangerine)] bg-[var(--surface-white)] border border-[var(--tangerine)] px-2 py-1 rounded' : 'text-[var(--text-secondary)]/60 hover:text-[var(--tangerine)]'}`}>
            <span className="material-symbols-outlined text-sm">{expanded ? 'expand_less' : 'expand_more'}</span>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-6 bg-[var(--surface-white)] grid grid-cols-12 gap-8">
          <div className="col-span-4 space-y-4">
            <h4 className="text-[10px] font-bold text-[var(--text-secondary)] dark:text-gray-400 uppercase tracking-widest flex items-center gap-2 justify-between">
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">lightbulb</span> Key Topics
              </span>
              <Link className="text-[10px] text-[var(--tangerine)] hover:underline font-bold normal-case ml-auto" to={`/coach/client/${client.clientId}/history`}>View sessions</Link>
            </h4>
            <div className="bg-[var(--off-white)] p-4 rounded-xl border border-[var(--border-light)] flex flex-wrap gap-2">
              {summary?.summary_metadata?.key_topics?.length > 0 ? (
                summary.summary_metadata.key_topics.map((topic, idx) => (
                  <span key={idx} className="px-2.5 py-1 bg-[var(--tangerine)]/10 text-[var(--tangerine)] dark:text-orange-400 rounded-full text-[10px] font-bold uppercase tracking-wider border border-[var(--tangerine)]/20 dark:border-orange-500/30">
                    {topic}
                  </span>
                ))
              ) : (
                <div className="space-y-3 w-full">
                  <div className="relative pl-3 border-l-2 border-[var(--tangerine)]">
                    <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase mb-0.5">What Happened</p>
                    <p className="text-xs text-[var(--text-primary)] leading-snug">{getInsight('whatHappened')}</p>
                  </div>
                  <div className="relative pl-3 border-l-2 border-[var(--tangerine)]">
                    <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase mb-0.5">Key Insight</p>
                    <p className="text-xs text-[var(--text-primary)] leading-snug">{getInsight('keyInsight')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="col-span-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] font-bold text-[var(--text-secondary)] dark:text-gray-400 uppercase tracking-widest flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">assignment_turned_in</span> Key Decisions
                </span>
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsSummaryModalOpen(true); }}
                  className="text-[10px] text-[var(--tangerine)] hover:underline font-bold normal-case ml-auto"
                >
                  See summary
                </button>
              </h4>
            </div>
            <div className="bg-[var(--surface-white)] p-4 rounded-xl border border-[var(--border-light)] shadow-sm space-y-2">
              {summary?.summary_metadata?.key_decisions?.length > 0 ? (
                summary.summary_metadata.key_decisions.map((decision, idx) => (
                  <div key={idx} className="p-3 bg-[var(--off-white)] rounded-lg border border-[var(--border-light)] text-xs text-[var(--text-primary)] leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{decision}</ReactMarkdown>
                  </div>
                ))
              ) : (
                <div className="space-y-3">
                  <div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--tangerine)]/20 text-[var(--tangerine)] mb-1">Goals</span>
                    <p className="text-xs text-[var(--text-primary)]">{getMemory('goals')}</p>
                  </div>
                  <div className="w-full h-px bg-[var(--border-light)]/30"></div>
                  <div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--tangerine)]/20 text-[var(--tangerine)] mb-1">Constraints</span>
                    <p className="text-xs text-[var(--text-primary)]">{getMemory('constraints')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="col-span-4 space-y-4">
            <h4 className="text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">send</span> Coach Follow-up
            </h4>
            <div className="bg-[var(--surface-white)] p-1 rounded-xl border border-[var(--border-light)] focus-within:ring-2 focus-within:ring-[var(--tangerine)] focus-within:border-transparent transition-all relative">
              <textarea 
                className="w-full border-0 rounded-lg p-3 text-sm text-[var(--text-primary)] placeholder-gray-400 focus:ring-0 resize-none h-32 bg-transparent" 
                placeholder="Write a follow-up message..."
                value={followUp}
                onChange={(e) => {
                  setFollowUp(e.target.value);
                  if (saveStatus !== 'idle') setSaveStatus('idle');
                }}
              ></textarea>
              <div className="flex items-center justify-between px-3 pb-2 pt-1 border-t border-[var(--border-light)]">
                <button className="text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--tangerine)] flex items-center gap-1 transition-colors">
                  <span className="material-symbols-outlined text-sm">auto_awesome</span> AI Assist
                </button>
                <div className="flex items-center gap-2">
                  {saveStatus === 'success' && <span className="text-[10px] text-emerald-500 font-bold flex items-center gap-1 animate-in fade-in slide-in-from-right-2"><span className="material-symbols-outlined text-sm">check_circle</span> Saved</span>}
                  {saveStatus === 'error' && <span className="text-[10px] text-red-500 font-bold flex items-center gap-1 animate-in fade-in slide-in-from-right-2"><span className="material-symbols-outlined text-sm">error</span> Failed</span>}
                  
                  <button 
                    onClick={handleSaveFollowUp}
                    disabled={isSaving || followUp === (client.followUp || '')}
                    className="bg-[var(--tangerine)] hover:bg-[var(--tangerine)]/90 disabled:opacity-40 disabled:hover:bg-[var(--tangerine)] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all flex items-center gap-1.5"
                  >
                    {isSaving ? (
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <span className="material-symbols-outlined text-sm">save</span>
                    )}
                    {isSaving ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {showClientStats && (
            <div className="col-span-12 border-t border-[var(--border-light)] pt-6 mt-2">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-[var(--text-primary)] uppercase tracking-widest flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm text-tangerine">monitoring</span>
                  Advanced Analytics
                </h4>
                <button 
                  onClick={() => setShowClientStats(false)}
                  className="text-[10px] text-[var(--text-secondary)] hover:text-[var(--tangerine)] font-bold"
                >
                  Close
                </button>
              </div>
              <ClientAnalytics userId={client.clientId} coachId={user?.id} />
            </div>
          )}

        </div>
      )}
      
      <SummaryModal 
        isOpen={isSummaryModalOpen} 
        onClose={() => setIsSummaryModalOpen(false)} 
        summary={summary} 
      />
    </div>
  );
};

export default ClientRow;
